import { useEffect, useState } from "react";
import { Table, Card } from "react-bootstrap";
import { GenericToggle } from "@components/FormsV2/GenericToggle";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import "./styles.css";
import {
  useV2ListNotificationTypesQuery,
  useV2ListNotificationPreferencesQuery,
  useV2CreateNotificationPreferenceMutation,
  useV2UpdateNotificationPreferenceMutation,
} from "@generated";

type Channel = "email" | "in_app";

type PreferenceOverride = {
  id: string;
  enabled: boolean;
};

const preferenceKey = (typeKey: string, channel: Channel) =>
  `${typeKey}:${channel}`;

const Notifications = (): JSX.Element => {
  const { data, isLoading } = useV2ListNotificationTypesQuery();
  const { data: preferencesData, isLoading: preferencesLoading } =
    useV2ListNotificationPreferencesQuery({ page: { size: 100 } });
  const [createPreference] = useV2CreateNotificationPreferenceMutation();
  const [updatePreference] = useV2UpdateNotificationPreferenceMutation();

  const [overrides, setOverrides] = useState<
    Record<string, PreferenceOverride>
  >({});
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  useEffect(() => {
    if (!preferencesData?.data) return;

    const initialOverrides: Record<string, PreferenceOverride> = {};
    preferencesData.data.forEach((preference) => {
      const typeKey = preference.attributes?.["type-key"];
      const channel = preference.attributes?.channel;
      if (!typeKey || !channel || !preference.id) return;

      initialOverrides[preferenceKey(typeKey, channel)] = {
        id: preference.id,
        enabled: !!preference.attributes?.enabled,
      };
    });
    setOverrides(initialOverrides);
  }, [preferencesData]);

  if (isLoading || preferencesLoading || !data) {
    return <ScopeStackSpinner />;
  }

  const valueFor = (
    type: NonNullable<typeof data.types>[number],
    channel: Channel
  ): boolean => {
    const override = type.key
      ? overrides[preferenceKey(type.key, channel)]
      : undefined;
    if (override) return override.enabled;

    return (
      type.account_channels?.[channel] ??
      type.default_channels?.[channel] ??
      false
    );
  };

  const handleToggle = async (
    typeKey: string,
    channel: Channel,
    value: boolean
  ) => {
    const key = preferenceKey(typeKey, channel);
    const previousOverride = overrides[key];

    setOverrides((prev) => ({
      ...prev,
      [key]: { id: previousOverride?.id ?? "", enabled: value },
    }));

    try {
      if (previousOverride?.id) {
        await updatePreference({
          id: previousOverride.id,
          body: {
            data: {
              id: previousOverride.id,
              type: "notification-preferences",
              attributes: {
                "type-key": typeKey,
                channel,
                enabled: value,
              },
            },
          },
        }).unwrap();
      } else {
        const result = await createPreference({
          body: {
            data: {
              type: "notification-preferences",
              attributes: {
                "type-key": typeKey,
                channel,
                enabled: value,
              },
            },
          },
        }).unwrap();

        const newId = result?.data?.id;
        if (newId) {
          setOverrides((prev) => ({
            ...prev,
            [key]: { id: newId, enabled: value },
          }));
        }
      }
      setShowSuccessAlert(true);
    } catch (error) {
      setOverrides((prev) => {
        const next = { ...prev };
        if (previousOverride) {
          next[key] = previousOverride;
        } else {
          delete next[key];
        }
        return next;
      });
      setErrorMessage(
        "Something went wrong saving this notification preference."
      );
      setShowFailAlert(true);
    }
  };

  const sortedCategories = [...(data.categories ?? [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  );

  return (
    <Card className="whiteBg notifications">
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        "Notification preferences updated",
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <Card.Header className="headerOverride">Notifications</Card.Header>
      <hr className="cardHeadDivider" style={{ marginBottom: "5px" }} />
      {sortedCategories.map((category) => {
        const typesForCategory = (data.types ?? []).filter(
          (type) => type.category === category.key
        );

        if (!typesForCategory.length) return null;

        return (
          <div key={category.key}>
            <Table className="whiteBg">
              <thead>
                <tr>
                  <th style={{ width: "70%", textAlign: "start" }}>
                    <div className="notificationCategory">{category.label}</div>
                  </th>
                  <th style={{ width: "15%", textAlign: "center" }}>In App</th>
                  <th style={{ width: "15%", textAlign: "center" }}>Email</th>
                </tr>
              </thead>
              <tbody>
                {typesForCategory.map((type) => {
                  const inAppValue = valueFor(type, "in_app");
                  const emailValue = valueFor(type, "email");

                  return (
                    <tr key={type.key}>
                      <td>
                        <div style={{ color: "#1c2655", fontSize: "15px" }}>
                          {type.name}
                        </div>
                        <div>{type.description}</div>
                      </td>
                      <td>
                        <GenericToggle
                          key={`${type.key}-in_app-${inAppValue}`}
                          initialValueOfIsOn={inAppValue}
                          onToggle={(value) =>
                            type.key && handleToggle(type.key, "in_app", value)
                          }
                          onText="On"
                          offText="Off"
                          hasText={false}
                        />
                      </td>
                      <td>
                        <GenericToggle
                          key={`${type.key}-email-${emailValue}`}
                          initialValueOfIsOn={emailValue}
                          onToggle={(value) =>
                            type.key && handleToggle(type.key, "email", value)
                          }
                          onText="On"
                          offText="Off"
                          hasText={false}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        );
      })}
    </Card>
  );
};

export default Notifications;
